import { useState, useEffect } from 'react'
import { Formik, Form } from 'formik'
import { socialAccountsAPI } from '../../services/api'
import { Button, FormikInput, Alert, Card, Spinner } from '../ui'
import { facebookConnectSchema } from '../../utils/validationSchemas'

const FacebookConnectPage = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [existingAccount, setExistingAccount] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    loadExistingAccount()
  }, [])

  const loadExistingAccount = async () => {
    try {
      setLoading(true)
      const response = await socialAccountsAPI.getAccountByProvider('facebook')
      
      if (response.data.success && response.data.data) {
        const account = response.data.data
        setExistingAccount(account)
        setIsConnected(account.is_connected === true)
      } else {
        setExistingAccount(null)
        setIsConnected(false)
      }
    } catch (err) {
      // If account not found (404), it's okay - just means not connected yet
      if (err.response?.status === 404) {
        setExistingAccount(null)
        setIsConnected(false)
      } else {
        console.error('Failed to load existing account:', err)
        setExistingAccount(null)
        setIsConnected(false)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsSubmitting(true)
      setError(null)
      setSuccess(null)

      const data = {
        provider: 'facebook',
        display_name: values.display_name,
        external_profile_id: values.external_profile_id,
        access_token: values.access_token,
      }

      let response
      if (existingAccount) {
        response = await socialAccountsAPI.updateAccount(existingAccount.id, data)
      } else {
        response = await socialAccountsAPI.createAccount(data)
      }

      if (response.data.success) {
        setSuccess(existingAccount ? 'Facebook account updated successfully!' : 'Facebook account added successfully!')
        await loadExistingAccount()
        // If account is now connected, the UI will update automatically
        if (response.data.data.is_connected) {
          setIsConnected(true)
        }
        resetForm({
          values: {
            display_name: response.data.data.display_name || '',
            external_profile_id: response.data.data.external_profile_id || '',
            access_token: '',
          },
        })
      } else {
        setError(response.data.message || 'Failed to save Facebook account')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to save Facebook account. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!existingAccount || !window.confirm('Are you sure you want to delete this Facebook account?')) {
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const response = await socialAccountsAPI.deleteAccount(existingAccount.id)
      
      if (response.data.success) {
        setSuccess('Facebook account deleted successfully!')
        setExistingAccount(null)
      } else {
        setError(response.data.message || 'Failed to delete Facebook account')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to delete Facebook account. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  // Show connected status if account is connected
  if (isConnected && existingAccount) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Facebook Business Profile</h1>
          <p className="text-gray-600 mt-1">
            Connect your Facebook Business Profile to manage your Meta assets and accounts.
          </p>
        </div>

        {error && (
          <div className="mb-4">
            <Alert variant="danger" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        {success && (
          <div className="mb-4">
            <Alert variant="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          </div>
        )}

        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Connected</h2>
                  <p className="text-sm text-gray-600">Your Facebook Business Profile is connected</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Active
              </span>
            </div>

            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Display Name</label>
                  <p className="mt-1 text-sm text-gray-900">{existingAccount.display_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Profile ID</label>
                  <p className="mt-1 text-sm text-gray-900">{existingAccount.external_profile_id}</p>
                </div>
                {existingAccount.meta?.user_name && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">User Name</label>
                    <p className="mt-1 text-sm text-gray-900">{existingAccount.meta.user_name}</p>
                  </div>
                )}
                {existingAccount.meta?.connected_at && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Connected At</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(existingAccount.meta.connected_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsConnected(false)}
              >
                Update Connection
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={loading}
              >
                Disconnect
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  const initialValues = {
    display_name: existingAccount?.display_name || '',
    external_profile_id: existingAccount?.external_profile_id || '',
    access_token: '',
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Facebook Business Profile</h1>
        <p className="text-gray-600 mt-1">
          Connect your Facebook Business Profile to manage your Meta assets and accounts.
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Card>
        <div className="p-6">
          <Formik
            initialValues={initialValues}
            validationSchema={facebookConnectSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting: formikSubmitting }) => (
              <Form className="space-y-6" noValidate>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    {existingAccount ? 'Update Facebook Account' : 'Add Facebook Account'}
                  </h2>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      <strong>How to get your Meta Business Profile ID and Access Token:</strong>
                    </p>
                    <ol className="list-decimal list-inside mt-2 text-sm text-blue-700 space-y-1">
                      <li>Go to Meta Business Suite or Facebook Developers</li>
                      <li>Navigate to your Business Settings</li>
                      <li>Find your Business Profile ID (also known as Business ID)</li>
                      <li>Generate a long-lived access token with the following permissions: business_management, pages_manage_posts, pages_read_engagement</li>
                      <li>Copy the Business Profile ID and Access Token to the fields below</li>
                    </ol>
                  </div>

                  <FormikInput
                    id="display_name"
                    name="display_name"
                    label="Display Name"
                    type="text"
                    placeholder="e.g., My Business"
                    disabled={isSubmitting || formikSubmitting}
                    required
                  />

                  <FormikInput
                    id="external_profile_id"
                    name="external_profile_id"
                    label="Meta Business Profile ID"
                    type="text"
                    placeholder="Enter your Meta Business Profile ID"
                    disabled={isSubmitting || formikSubmitting}
                    required
                  />

                  <FormikInput
                    id="access_token"
                    name="access_token"
                    label="Access Token"
                    type="password"
                    placeholder="Enter your Meta access token"
                    disabled={isSubmitting || formikSubmitting}
                    required
                  />

                  <div className="flex items-center justify-between pt-4">
                    <div className="flex space-x-3">
                      <Button
                        type="submit"
                        variant="primary"
                        loading={isSubmitting || formikSubmitting}
                        disabled={isSubmitting || formikSubmitting}
                      >
                        {existingAccount ? 'Update Account' : 'Add Account'}
                      </Button>
                      
                      {existingAccount && (
                        <Button
                          type="button"
                          variant="danger"
                          onClick={handleDelete}
                          disabled={isSubmitting || loading}
                        >
                          Delete Account
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Card>
    </div>
  )
}

export default FacebookConnectPage

